import { useComputed, useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import { useEffect, useRef } from "preact/hooks";
import {
  IcoArchive,
  IcoArrowsUpDown,
  IcoCalendar,
  IcoChevronLeft,
  IcoChevronRight,
  IcoEye,
  IcoEyeOff,
  IcoPlus,
  IcoSpinner,
  IcoUsers,
} from "../../../components/icons";
import {
  BrandedLayout,
  ConfirmDialog,
  type MemberOption,
  QuestionBankPicker,
  TextArea,
} from "../../../components/ui";
import { SlideOver } from "../../../components/ui/slide-over";
import { usePageTitle } from "../../../hooks";
import { rpc } from "../../../rpc";
import { showError } from "../../../stores/errors";
import type { MimicState } from "../../../stores/mimic";
import type { BankQuestion, BrandColor, Group, User } from "../../../types";
import { getErrorMessage } from "../../../utils/errors";
import { parseTargetIds } from "../../../utils/json";
import { AssignmentPanel } from "./panels/assignment-panel";
import { SchedulePanel } from "./panels/schedule-panel";
import {
  FormPreview,
  type Question,
  QuestionList,
  type QuestionViewMode,
  QueueSection,
  ReorderList,
} from "./shared";

type OrgInfo = {
  id: string;
  name: string;
  logoUrl?: string | null;
};

type FormData = {
  id: string;
  orgId: string;
  title: string;
  rrule: string | null;
  startsAt: Date | null;
  endsAt: Date | null;
  submissionWindowDays: number | null;
  prioritiesEnabled: boolean;
  valuesRecognitionEnabled: boolean;
  questions: Question[];
  assignedMemberIds: string[];
  excludedMemberIds: string[];
  assignmentMode: "all" | "non_admins" | "choose" | null;
  archivedAt: Date | null;
};

type QueueSettings = {
  questionsPerPeriod: number;
  repeatAfterComplete: boolean;
};

type EditFormPageState = {
  currentUser: User;
  mimic: MimicState;
  form: FormData;
  org: OrgInfo;
  brandColor: BrandColor;
  hasReports: boolean;
  hasCoreValues: boolean;
  members: MemberOption[];
  groups: Group[];
  groupMemberships: Record<string, string[]>; // groupId -> memberIds
  queueSettings: QueueSettings;
  queueItems: {
    questionId: string;
    showCount: number;
    lastShownAt: string | Date | null | undefined;
  }[];
  currentMemberId: string;
};

export async function load(args: LoaderArgs): Promise<EditFormPageState> {
  const formId = args.params.formId ?? "";

  // Check auth first to get a clean redirect for unauthenticated users
  const [authResult, orgsResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
  ]);
  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  const formResult = await rpc.forms.get({ id: formId });

  // Get brand color from list endpoint
  const orgFromList = orgsResult.organizations.find(
    (o) => o.id === formResult.org.id,
  );
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  // Load groups, memberships, queue settings, and core values
  const [groupsResult, membershipsResult, queueResult, valuesResult] =
    await Promise.all([
      rpc.groups.list({ workspaceId: formResult.org.id }),
      rpc.groups.getAllMemberships({ workspaceId: formResult.org.id }),
      rpc.forms.getQueue({ formId }),
      rpc.coreValues.list({ workspaceId: formResult.org.id }),
    ]);

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    brandColor,
    hasReports: orgFromList?.hasReports ?? false,
    hasCoreValues: (valuesResult.values ?? []).length > 0,
    form: {
      id: formResult.form.id,
      orgId: formResult.form.orgId,
      title: formResult.form.title,
      rrule: formResult.form.rrule ?? null,
      startsAt: formResult.form.startsAt ?? null,
      endsAt: formResult.form.endsAt ?? null,
      submissionWindowDays: formResult.form.submissionWindowDays ?? null,
      prioritiesEnabled: formResult.form.prioritiesEnabled,
      valuesRecognitionEnabled:
        formResult.form.valuesRecognitionEnabled ?? false,
      questions: formResult.questions.map((q) => ({
        id: q.id,
        title: q.title,
        description: q.description,
        type: q.type,
        ratingMax: q.ratingMax,
        archived: q.archived,
        seq: q.seq,
        targetType: ((q as { targetType?: string }).targetType ??
          "company_wide") as Question["targetType"],
        targetIds: parseTargetIds((q as { targetIds?: unknown }).targetIds),
        frequency: ((q as { frequency?: string }).frequency ??
          "every") as Question["frequency"],
        category: (q as { category?: string | null }).category ?? null,
        required: (q as { required?: boolean }).required ?? false,
        allowMultiple:
          (q as { allowMultiple?: boolean }).allowMultiple ?? false,
      })),
      assignedMemberIds: formResult.assignedMemberIds,
      excludedMemberIds: formResult.excludedMemberIds ?? [],
      assignmentMode: deriveAssignmentMode(
        formResult.assignedMemberIds,
        formResult.members,
        formResult.excludedMemberIds ?? [],
      ),
      archivedAt: formResult.form.archivedAt ?? null,
    },
    org: formResult.org,
    members: formResult.members,
    groups: groupsResult.groups,
    groupMemberships: membershipsResult.memberships,
    queueSettings: queueResult.queue,
    queueItems: queueResult.items,
    currentMemberId: formResult.currentMemberId,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle(`Edit ${state.value.form.title}`);
  const {
    currentUser,
    mimic,
    form: initialForm,
    org,
    brandColor,
    hasReports,
    hasCoreValues,
    members,
    groups,
    groupMemberships,
    queueSettings: initialQueueSettings,
    queueItems: initialQueueItems,
    currentMemberId,
  } = state.value;

  // Form state - current edits
  const formState = useSignal<FormData>(initialForm);
  // Saved state - what's in the database
  const savedState = useSignal<FormData>(initialForm);

  // Queue settings state
  const queueSettings = useSignal(initialQueueSettings);

  // Queue items for showing rotation stats per question
  const queueItems = useSignal(initialQueueItems);

  // UI state
  const showArchived = useSignal(false);
  const reorderMode = useSignal(false);
  const editingId = useSignal<string | null>(null);
  const questionViewMode = useSignal<QuestionViewMode>("grouped");
  const saving = useSignal(false);
  const saveStatus = useSignal<"idle" | "saving" | "saved" | "error">("idle");
  const showCancelDialog = useSignal(false);
  const showArchiveDialog = useSignal(false);
  const archiving = useSignal(false);

  // Slide-over state
  const activePanel = useSignal<"schedule" | "assignment" | "preview" | null>(
    null,
  );
  const showBankPicker = useSignal(false);

  // Dirty state detection
  const isDirty = useComputed(() => {
    const current = formState.value;
    const saved = savedState.value;

    if (current.title !== saved.title) return true;
    if (current.rrule !== saved.rrule) return true;
    if (!datesEqual(current.startsAt, saved.startsAt)) return true;
    if (!datesEqual(current.endsAt, saved.endsAt)) return true;
    if (current.prioritiesEnabled !== saved.prioritiesEnabled) return true;
    if (current.valuesRecognitionEnabled !== saved.valuesRecognitionEnabled)
      return true;
    if (current.submissionWindowDays !== saved.submissionWindowDays)
      return true;
    if (!arraysEqual(current.assignedMemberIds, saved.assignedMemberIds))
      return true;
    if (!arraysEqual(current.excludedMemberIds, saved.excludedMemberIds))
      return true;
    if (current.assignmentMode !== saved.assignmentMode) return true;
    if (!questionsEqual(current.questions, saved.questions)) return true;

    return false;
  });

  const visibleQuestions = useComputed(() => {
    const qs = formState.value.questions.filter(
      (q) => showArchived.value || !q.archived,
    );
    return qs.sort((a, b) => a.seq - b.seq);
  });

  const archivedCount = useComputed(
    () => formState.value.questions.filter((q) => q.archived).length,
  );

  const activeQuestions = useComputed(() =>
    formState.value.questions.filter((q) => !q.archived),
  );

  // Title editing
  const setTitle = (title: string) => {
    formState.value = { ...formState.value, title };
  };

  // Question editing
  const addQuestion = () => {
    const newId = crypto.randomUUID();
    const maxSeq = Math.max(0, ...formState.value.questions.map((q) => q.seq));
    const newQuestion: Question = {
      id: newId,
      title: "",
      description: "",
      type: "short_answer",
      ratingMax: 5,
      archived: false,
      seq: maxSeq + 1,
      targetType: "company_wide",
      targetIds: [],
      frequency: "every",
      category: null,
      required: true,
      allowMultiple: false,
    };
    formState.value = {
      ...formState.value,
      questions: [...formState.value.questions, newQuestion],
    };
    editingId.value = newId;
  };

  const addQuestionsFromBank = (bankQuestions: BankQuestion[]) => {
    if (bankQuestions.length === 0) return;

    let maxSeq = Math.max(0, ...formState.value.questions.map((q) => q.seq));
    const newQuestions: Question[] = bankQuestions.map((bq) => {
      maxSeq += 1;
      return {
        id: crypto.randomUUID(),
        title: bq.title,
        description: bq.description,
        type: bq.type,
        ratingMax: bq.ratingMax,
        archived: false,
        seq: maxSeq,
        targetType: "company_wide" as const,
        targetIds: [],
        frequency: "every" as const,
        category: null,
        required: true,
        allowMultiple: false,
      };
    });

    formState.value = {
      ...formState.value,
      questions: [...formState.value.questions, ...newQuestions],
    };

    // Open the first added question for editing
    const firstQuestion = newQuestions[0];
    if (firstQuestion) {
      editingId.value = firstQuestion.id;
    }
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    const updatedQuestions = formState.value.questions.map((q) =>
      q.id === id ? { ...q, ...updates } : q,
    );
    formState.value = { ...formState.value, questions: updatedQuestions };
  };

  const archiveQuestion = (id: string) => {
    updateQuestion(id, { archived: true });
    if (editingId.value === id) {
      editingId.value = null;
    }
  };

  const restoreQuestion = (id: string) => {
    updateQuestion(id, { archived: false });
  };

  const handleReorder = (reorderedQuestions: Question[]) => {
    formState.value = { ...formState.value, questions: reorderedQuestions };
  };

  const handleDoneReorder = () => {
    reorderMode.value = false;
  };

  // Ref for timeout cleanup
  const saveStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Save entire form
  const handleSave = async () => {
    if (saving.value) return;

    // Exit reorder mode if active - provides clearer UX
    if (reorderMode.value) {
      reorderMode.value = false;
    }

    saving.value = true;
    saveStatus.value = "saving";

    // Clear any pending status reset
    if (saveStatusTimeoutRef.current) {
      clearTimeout(saveStatusTimeoutRef.current);
      saveStatusTimeoutRef.current = null;
    }

    try {
      await rpc.forms.save({
        formId: formState.value.id,
        title: formState.value.title || "Untitled form",
        rrule: formState.value.rrule,
        startsAt: formState.value.startsAt,
        endsAt: formState.value.endsAt,
        submissionWindowDays: formState.value.submissionWindowDays,
        prioritiesEnabled: formState.value.prioritiesEnabled,
        valuesRecognitionEnabled: formState.value.valuesRecognitionEnabled,
        questions: formState.value.questions,
        assignmentMode: formState.value.assignmentMode,
        assignedMemberIds: formState.value.assignedMemberIds,
        excludedMemberIds: formState.value.excludedMemberIds,
      });

      // Sync queue items with questions that have frequency='queued'
      await rpc.forms.syncQueueItems({ formId: formState.value.id });

      savedState.value = formState.value;
      saveStatus.value = "saved";
      // Reset status after a delay
      saveStatusTimeoutRef.current = setTimeout(() => {
        if (saveStatus.value === "saved") {
          saveStatus.value = "idle";
        }
        saveStatusTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      showError(getErrorMessage(err));
      saveStatus.value = "error";
    } finally {
      saving.value = false;
    }
  };

  // Ref for keyboard handler to avoid stale closure
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  // Queue settings change handler
  const handleQueueSettingsChange = async (settings: QueueSettings) => {
    try {
      await rpc.forms.saveQueueSettings({
        formId: formState.value.id,
        questionsPerPeriod: settings.questionsPerPeriod,
        repeatAfterComplete: settings.repeatAfterComplete,
      });
      queueSettings.value = settings;
    } catch (err) {
      showError(getErrorMessage(err));
    }
  };

  // Archive / unarchive form
  const isArchived = formState.value.archivedAt !== null;

  const handleArchiveToggle = async () => {
    if (archiving.value) return;
    archiving.value = true;
    try {
      const newArchived = !isArchived;
      await rpc.forms.setArchived({
        formId: formState.value.id,
        archived: newArchived,
      });
      navigateTo(`/orgs/${org.id}/forms`);
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      archiving.value = false;
      showArchiveDialog.value = false;
    }
  };

  // Cancel and go back
  const handleCancel = () => {
    if (isDirty.value) {
      showCancelDialog.value = true;
    } else {
      navigateTo(`/orgs/${org.id}/forms`);
    }
  };

  // Keyboard shortcut for save (Cmd+S / Ctrl+S) and timeout cleanup
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        handleSaveRef.current();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (saveStatusTimeoutRef.current) {
        clearTimeout(saveStatusTimeoutRef.current);
      }
    };
  }, []);

  // Warn before navigating away with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty.value) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const scheduleSummary = getScheduleSummary(
    formState.value.rrule,
    formState.value.startsAt,
    formState.value.endsAt,
  );

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={org.id}
      orgName={org.name}
      brandColor={brandColor}
      activeTab="forms"
      isAdmin={true}
      hasReports={hasReports}
    >
      <div class="flex justify-center pb-20">
        <div class="w-full max-w-2xl flex flex-col gap-8">
          <a
            href={`/orgs/${org.id}/forms`}
            class="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text"
          >
            <IcoChevronLeft class="-ml-[5px] size-4" />
            Back to Forms
          </a>

          {/* Header with editable title */}
          <div class="flex flex-col gap-1">
            <TextArea
              value={formState.value.title}
              placeholder="Untitled form"
              onInput={(e) => {
                setTitle((e.target as HTMLTextAreaElement).value);
              }}
              class="text-2xl font-bold text-text bg-transparent border-0 p-0 resize-none focus:outline-none focus:ring-0"
            />
            <div class="flex items-center gap-3">
              <p class="text-sm text-text-muted">{org.name}</p>
              <span class="text-text-subtle">&middot;</span>
              <button
                type="button"
                onClick={() => {
                  activePanel.value = "preview";
                }}
                class="text-sm font-medium text-text-secondary hover:text-text transition-colors inline-flex items-center gap-1"
              >
                <IcoEye class="size-4" />
                Preview
              </button>
              <span class="text-text-subtle">&middot;</span>
              <a
                href={`/orgs/${org.id}/forms/${formState.value.id}/results`}
                class="text-sm font-medium text-text-secondary hover:text-text transition-colors"
              >
                View Results
              </a>
              <span class="text-text-subtle">&middot;</span>
              <button
                type="button"
                onClick={() => {
                  showArchiveDialog.value = true;
                }}
                class="text-sm font-medium text-text-secondary hover:text-text transition-colors inline-flex items-center gap-1"
              >
                <IcoArchive class="size-4" />
                {isArchived ? "Unarchive" : "Archive"}
              </button>
            </div>
          </div>

          {/* ============================================================ */}
          {/* DISTRIBUTION SECTION */}
          {/* ============================================================ */}
          <section>
            <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-3">
              Distribution
            </h2>
            <div class="rounded-xl border border-border bg-surface divide-y divide-border overflow-hidden">
              {/* Recipients row */}
              <button
                type="button"
                onClick={() => {
                  activePanel.value = "assignment";
                }}
                class="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-hover transition-colors group"
              >
                <IcoUsers class="size-5 text-text-muted" />
                <span class="text-sm text-text-muted min-w-[80px]">
                  Recipients
                </span>
                <span class="flex-1 text-right text-sm font-medium text-text group-hover:text-[var(--brand-600)] transition-colors">
                  {formState.value.assignedMemberIds.length === 0 &&
                  formState.value.excludedMemberIds.length === 0
                    ? "None"
                    : formState.value.assignmentMode === "all" &&
                        formState.value.excludedMemberIds.length > 0
                      ? `Everyone (${formState.value.excludedMemberIds.length} excluded)`
                      : formState.value.assignmentMode === "non_admins" &&
                          formState.value.excludedMemberIds.length > 0
                        ? `Non-admins (${formState.value.excludedMemberIds.length} excluded)`
                        : formState.value.assignedMemberIds.length ===
                            members.length
                          ? `Everyone (${members.length})`
                          : `${formState.value.assignedMemberIds.length} of ${members.length}`}
                </span>
                <IcoChevronRight class="size-4 text-text-subtle" />
              </button>

              {/* Schedule row */}
              <button
                type="button"
                onClick={() => {
                  activePanel.value = "schedule";
                }}
                class="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-hover transition-colors group"
              >
                <IcoCalendar class="size-5 text-text-muted" />
                <span class="text-sm text-text-muted min-w-[80px]">
                  Schedule
                </span>
                <span class="flex-1 text-right text-sm font-medium text-text group-hover:text-[var(--brand-600)] transition-colors">
                  {scheduleSummary}
                </span>
                <IcoChevronRight class="size-4 text-text-subtle" />
              </button>
            </div>
          </section>

          {/* ============================================================ */}
          {/* CONTENT SECTION */}
          {/* ============================================================ */}
          <section>
            <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide mb-3">
              Content
            </h2>
            <div class="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border">
              {/* Priorities toggle */}
              <button
                type="button"
                onClick={() => {
                  formState.value = {
                    ...formState.value,
                    prioritiesEnabled: !formState.value.prioritiesEnabled,
                  };
                }}
                class="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-hover transition-colors"
              >
                <span class="text-sm text-text">
                  Include priorities section
                </span>
                {/* Toggle switch */}
                <div
                  class={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${
                    formState.value.prioritiesEnabled
                      ? "bg-[var(--brand-600)]"
                      : "bg-surface-muted"
                  }`}
                >
                  <div
                    class={`absolute top-1 left-1 size-4 rounded-full bg-white shadow-sm transition-transform ${
                      formState.value.prioritiesEnabled ? "translate-x-4" : ""
                    }`}
                  />
                </div>
              </button>

              {/* Values recognition toggle */}
              <button
                type="button"
                onClick={() => {
                  if (!hasCoreValues) return;
                  formState.value = {
                    ...formState.value,
                    valuesRecognitionEnabled:
                      !formState.value.valuesRecognitionEnabled,
                  };
                }}
                class={`w-full flex items-center justify-between gap-3 px-4 py-3 transition-colors ${
                  hasCoreValues
                    ? "hover:bg-surface-hover"
                    : "pointer-events-none opacity-50"
                }`}
              >
                <div class="flex flex-col items-start gap-0.5">
                  <span class="text-sm text-text">Values recognition</span>
                  <span class="text-xs text-text-muted">
                    {hasCoreValues
                      ? "Allow members to recognize teammates for core values"
                      : "Add core values in the Values page to enable recognition"}
                  </span>
                </div>
                <div
                  class={`relative w-10 h-6 rounded-full transition-colors flex-shrink-0 ${
                    hasCoreValues && formState.value.valuesRecognitionEnabled
                      ? "bg-[var(--brand-600)]"
                      : "bg-surface-muted"
                  }`}
                >
                  <div
                    class={`absolute top-1 left-1 size-4 rounded-full bg-white shadow-sm transition-transform ${
                      hasCoreValues && formState.value.valuesRecognitionEnabled
                        ? "translate-x-4"
                        : ""
                    }`}
                  />
                </div>
              </button>
            </div>
          </section>

          {/* Queue rotation section - only shows when there are queued questions */}
          <QueueSection
            questions={formState.value.questions}
            questionsPerPeriod={queueSettings.value.questionsPerPeriod}
            repeatAfterComplete={queueSettings.value.repeatAfterComplete}
            onSettingsChange={handleQueueSettingsChange}
          />

          {/* ============================================================ */}
          {/* QUESTIONS SECTION */}
          {/* ============================================================ */}
          <section>
            <div class="flex items-center justify-between mb-3">
              <h2 class="text-sm font-semibold text-text-muted uppercase tracking-wide">
                Questions
                {activeQuestions.value.length > 0 && (
                  <span class="ml-1.5 font-normal">
                    ({activeQuestions.value.length})
                  </span>
                )}
              </h2>
              <div class="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    questionViewMode.value =
                      questionViewMode.value === "grouped" ? "flat" : "grouped";
                  }}
                  class="text-xs text-text-subtle hover:text-text-muted transition-colors"
                >
                  {questionViewMode.value === "grouped"
                    ? "Show flat list"
                    : "Group by category"}
                </button>
                {archivedCount.value > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      showArchived.value = !showArchived.value;
                    }}
                    class="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-text transition-colors"
                  >
                    {showArchived.value ? (
                      <IcoEyeOff class="size-4" />
                    ) : (
                      <IcoEye class="size-4" />
                    )}
                    {showArchived.value ? "Hide" : "Show"} archived (
                    {archivedCount.value})
                  </button>
                )}
                {visibleQuestions.value.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      if (reorderMode.value) {
                        handleDoneReorder();
                      } else {
                        reorderMode.value = true;
                      }
                    }}
                    class={`inline-flex items-center gap-1.5 text-sm transition-colors ${
                      reorderMode.value
                        ? "text-[var(--brand-600)] font-medium"
                        : "text-text-muted hover:text-text"
                    }`}
                  >
                    <IcoArrowsUpDown class="size-4" />
                    {reorderMode.value ? "Done" : "Reorder"}
                  </button>
                )}
              </div>
            </div>

            {/* Question list */}
            {reorderMode.value ? (
              <ReorderList
                questions={formState.value.questions}
                showArchived={showArchived.value}
                onReorder={handleReorder}
              />
            ) : (
              <QuestionList
                questions={visibleQuestions.value}
                editingId={editingId}
                groups={groups}
                members={members}
                viewMode={questionViewMode.value}
                queueItemStats={queueItems.value}
                onSave={updateQuestion}
                onArchive={archiveQuestion}
                onRestore={restoreQuestion}
              />
            )}

            {/* Add questions button */}
            {!reorderMode.value && (
              <button
                type="button"
                onClick={() => {
                  showBankPicker.value = true;
                }}
                class="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-border bg-surface-hover/50 hover:bg-surface-hover hover:border-text-subtle transition-colors text-text-muted hover:text-text"
              >
                <IcoPlus class="size-4" />
                <span class="text-sm font-medium">Add Questions</span>
              </button>
            )}
          </section>
        </div>
      </div>

      {/* Fixed save footer */}
      <div class="fixed bottom-0 left-0 right-0 bg-surface border-t border-border z-50">
        <div class="max-w-2xl mx-auto px-4 py-3 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving.value || !isDirty.value}
            class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving.value && <IcoSpinner class="size-4 animate-spin" />}
            Save Changes
          </button>
          <button
            type="button"
            onClick={handleCancel}
            class="px-4 py-2 rounded-lg text-sm font-medium text-text-muted border border-border hover:bg-surface-hover transition-colors"
          >
            Cancel
          </button>
          {saveStatus.value !== "idle" && (
            <span class="text-sm text-text-muted">
              {saveStatus.value === "saving" && "Saving..."}
              {saveStatus.value === "saved" && (
                <span class="text-green-600 dark:text-green-400">Saved</span>
              )}
              {saveStatus.value === "error" && (
                <span class="text-red-600 dark:text-red-400">Error saving</span>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Cancel confirmation dialog */}
      <ConfirmDialog
        open={showCancelDialog.value}
        title="Unsaved changes"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        onConfirm={() => {
          showCancelDialog.value = false;
          navigateTo(`/orgs/${org.id}/forms`);
        }}
        onCancel={() => {
          showCancelDialog.value = false;
        }}
      >
        You have unsaved changes. Are you sure you want to discard them?
      </ConfirmDialog>

      {/* Archive confirmation dialog */}
      <ConfirmDialog
        open={showArchiveDialog.value}
        title={isArchived ? "Unarchive this form?" : "Archive this form?"}
        confirmLabel={isArchived ? "Unarchive" : "Archive"}
        cancelLabel="Cancel"
        onConfirm={handleArchiveToggle}
        onCancel={() => {
          showArchiveDialog.value = false;
        }}
      >
        {isArchived
          ? "This form will be moved back to the active forms list."
          : "This form will be moved to the archived section. It can be unarchived later."}
      </ConfirmDialog>

      {/* Schedule slide-over */}
      <SlideOver
        open={activePanel.value === "schedule"}
        onClose={() => {
          activePanel.value = null;
        }}
        title="Schedule"
      >
        <SchedulePanel
          rrule={formState.value.rrule}
          startsAt={formState.value.startsAt}
          endsAt={formState.value.endsAt}
          submissionWindowDays={formState.value.submissionWindowDays}
          onChange={(updates) => {
            formState.value = {
              ...formState.value,
              rrule: updates.rrule ?? formState.value.rrule,
              startsAt: updates.startsAt ?? formState.value.startsAt,
              endsAt: updates.endsAt ?? formState.value.endsAt,
              submissionWindowDays:
                updates.submissionWindowDays !== undefined
                  ? updates.submissionWindowDays
                  : formState.value.submissionWindowDays,
            };
          }}
          onClose={() => {
            activePanel.value = null;
          }}
        />
      </SlideOver>

      {/* Assignment slide-over */}
      <SlideOver
        open={activePanel.value === "assignment"}
        onClose={() => {
          activePanel.value = null;
        }}
        title="Assignment"
      >
        <AssignmentPanel
          assignedMemberIds={formState.value.assignedMemberIds}
          excludedMemberIds={formState.value.excludedMemberIds}
          members={members}
          groups={groups}
          groupMemberships={new Map(Object.entries(groupMemberships))}
          currentMemberId={currentMemberId}
          onChange={(memberIds, mode, excludedIds) => {
            formState.value = {
              ...formState.value,
              assignedMemberIds: memberIds,
              assignmentMode: mode,
              excludedMemberIds: excludedIds ?? [],
            };
          }}
          onClose={() => {
            activePanel.value = null;
          }}
        />
      </SlideOver>

      {/* Preview slide-over */}
      <SlideOver
        open={activePanel.value === "preview"}
        onClose={() => {
          activePanel.value = null;
        }}
        title="Form Preview"
      >
        <FormPreview
          title={formState.value.title}
          questions={formState.value.questions}
        />
      </SlideOver>

      {/* Question bank picker */}
      <QuestionBankPicker
        open={showBankPicker.value}
        onClose={() => {
          showBankPicker.value = false;
        }}
        workspaceId={org.id}
        onAdd={addQuestionsFromBank}
        onCreateCustom={addQuestion}
      />
    </BrandedLayout>
  );
}

function getScheduleSummary(
  rrule?: string | null,
  startsAt?: Date | null,
  endsAt?: Date | null,
): string {
  if (!rrule) {
    if (startsAt || endsAt) {
      return "One time";
    }
    return "Not scheduled";
  }

  if (rrule.includes("FREQ=MONTHLY")) {
    return "Monthly";
  }

  const isBiweekly = rrule.includes("INTERVAL=2");
  const freqLabel = isBiweekly ? "Biweekly" : "Weekly";

  if (rrule.includes("FREQ=WEEKLY")) {
    const byDayMatch = rrule.match(/BYDAY=([A-Z,]+)/);
    if (byDayMatch?.[1]) {
      const dayCodes = byDayMatch[1].split(",");
      const daySet = new Set(dayCodes);

      // Check for weekdays (Mon-Fri)
      const weekdays = ["MO", "TU", "WE", "TH", "FR"];
      const isWeekdays =
        daySet.size === 5 && weekdays.every((d) => daySet.has(d));
      if (isWeekdays) {
        return `${freqLabel} on weekdays`;
      }

      // Check for weekends (Sat-Sun)
      const weekends = ["SA", "SU"];
      const isWeekends =
        daySet.size === 2 && weekends.every((d) => daySet.has(d));
      if (isWeekends) {
        return `${freqLabel} on weekends`;
      }

      // Single day - show the day name
      const firstDay = dayCodes[0];
      if (dayCodes.length === 1 && firstDay) {
        const dayMap: Record<string, string> = {
          SU: "Sun",
          MO: "Mon",
          TU: "Tue",
          WE: "Wed",
          TH: "Thu",
          FR: "Fri",
          SA: "Sat",
        };
        return `${freqLabel} on ${dayMap[firstDay] ?? firstDay}`;
      }

      // Multiple days - show count
      return `${freqLabel} (${dayCodes.length} days)`;
    }
    return freqLabel;
  }

  return "Scheduled";
}

// Helper functions for dirty state comparison

function datesEqual(a: Date | null, b: Date | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return new Date(a).getTime() === new Date(b).getTime();
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, i) => val === sortedB[i]);
}

/**
 * Derive the assignment mode from member IDs and the full member list.
 * Used at load time so we don't depend on the stored DB value, which may
 * be null (old forms) or stale (mode changed but form not re-saved).
 */
function deriveAssignmentMode(
  assignedMemberIds: string[],
  members: { id: string; role: string }[],
  excludedMemberIds: string[] = [],
): FormData["assignmentMode"] {
  if (assignedMemberIds.length === 0 && excludedMemberIds.length === 0)
    return null;

  const excludedSet = new Set(excludedMemberIds);
  const allIds = new Set(
    members.filter((m) => !excludedSet.has(m.id)).map((m) => m.id),
  );
  const nonAdminIds = new Set(
    members
      .filter((m) => m.role !== "admin" && !excludedSet.has(m.id))
      .map((m) => m.id),
  );
  const assignedSet = new Set(assignedMemberIds);

  if (
    allIds.size === assignedSet.size &&
    [...allIds].every((id) => assignedSet.has(id))
  ) {
    return "all";
  }

  if (
    nonAdminIds.size > 0 &&
    nonAdminIds.size === assignedSet.size &&
    [...nonAdminIds].every((id) => assignedSet.has(id))
  ) {
    return "non_admins";
  }

  return "choose";
}

function questionsEqual(a: Question[], b: Question[]): boolean {
  if (a.length !== b.length) return false;

  const sortedA = [...a].sort((x, y) => x.id.localeCompare(y.id));
  const sortedB = [...b].sort((x, y) => x.id.localeCompare(y.id));

  return sortedA.every((qa, i) => {
    const qb = sortedB[i];
    if (!qb) return false;
    return (
      qa.id === qb.id &&
      qa.title === qb.title &&
      qa.description === qb.description &&
      qa.type === qb.type &&
      qa.ratingMax === qb.ratingMax &&
      qa.archived === qb.archived &&
      qa.seq === qb.seq &&
      qa.targetType === qb.targetType &&
      arraysEqual(qa.targetIds, qb.targetIds) &&
      qa.frequency === qb.frequency &&
      qa.category === qb.category &&
      qa.required === qb.required &&
      qa.allowMultiple === qb.allowMultiple
    );
  });
}
